import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { resolveBackendImage } from '../utils/brandLogo';

/**
 * BackendImage — wrapper único para `<img>` consumindo assets do backend.
 *
 * Recursos:
 *   • Resolve `src` automaticamente via `resolveBackendImage`:
 *       - URL relativa → prefixa REACT_APP_BACKEND_URL
 *       - URL absoluta interna /api/uploads/... → rebases pro host atual
 *       - URL externa (Unsplash, R2, CDN) → preserva
 *       - data: URI → preserva
 *   • Lazy loading nativo (`loading="lazy"`) por padrão — melhora LCP/INP.
 *   • Placeholder cinza + fade-in é OPT-IN via `showPlaceholder` para não
 *     quebrar logos transparentes (PNG) e cards (que já têm bg do container).
 *   • Detecta cache hit (`img.complete`) no mount: imagens já em cache não
 *     piscam placeholder.
 *   • `onError`: por padrão chama callback opcional. Se prop `hideOnError` for
 *     true (default), esconde a tag para evitar ícone "broken image" feio.
 *   • Aceita TODAS as props de `<img>` por spread — drop-in replacement.
 *
 * Quando NÃO usar:
 *   • Tags `<img>` que precisam de `crossOrigin` específico para canvas API.
 *   • Tags com `onError` que faz fallback para outra src (use `<img>` direto).
 *   • Tags em SVG/PDF/canvas (`<image>` é diferente).
 *
 * @example
 *   <BackendImage src={business.avatar_url} alt={business.nome} className="..." />
 */
const BackendImage = forwardRef(function BackendImage(
  {
    src,
    alt = '',
    className = '',
    fallback = null,
    hideOnError = true,
    eager = false,
    fetchPriority,
    width,
    height,
    aspectRatio,
    showPlaceholder = false, // opt-in: shimmer cinza + fade-in enquanto carrega
    onLoad,
    onError,
    style,
    ...rest
  },
  ref,
) {
  const [status, setStatus] = useState('loading'); // loading | loaded | error
  const internalRef = useRef(null);
  const imgRef = ref || internalRef;

  const resolved = resolveBackendImage(src);

  // Reseta status quando o src muda — evita ficar travado em 'error' após troca.
  // Também detecta cache hit (img.complete=true) para não exibir placeholder
  // em imagens já decodificadas pelo browser.
  useEffect(() => {
    if (!resolved) { setStatus('error'); return; }
    const node = imgRef.current;
    if (node && node.complete && node.naturalWidth > 0) {
      setStatus('loaded');
    } else {
      setStatus('loading');
    }
  }, [resolved, imgRef]);

  if (!resolved || status === 'error') {
    if (fallback !== null) return fallback;
    if (hideOnError) return null;
    return <div className={className} aria-label={alt || 'imagem indisponível'} />;
  }

  const isLoaded = status === 'loaded';

  // Reserva visual de espaço (evita Cumulative Layout Shift):
  //   • `width`/`height` viram atributos HTML — o browser calcula o
  //     aspect-ratio automático e mantém o slot enquanto a img baixa.
  //   • `aspectRatio` (string CSS) tem prioridade se informado.
  //   • Quando nenhum dos 3 for passado, comportamento atual é mantido.
  const sizeStyle = aspectRatio
    ? { aspectRatio }
    : (width && height ? { aspectRatio: `${width} / ${height}` } : null);

  // Placeholder visual (cinza + fade) é OPT-IN para evitar quebrar logos
  // com fundo transparente e thumbnails de cards. Comportamento padrão é
  // idêntico ao <img> nativo do browser.
  const placeholderStyle = showPlaceholder && !isLoaded
    ? {
        backgroundColor: '#f3f4f6',
        transition: 'opacity 0.25s ease, background-color 0.25s ease',
        opacity: 0.6,
      }
    : null;

  return (
    <img
      ref={imgRef}
      src={resolved}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      {...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
      {...(width ? { width } : {})}
      {...(height ? { height } : {})}
      className={className}
      style={{
        ...(placeholderStyle || {}),
        ...(sizeStyle || {}),
        ...style,
      }}
      onLoad={(e) => { setStatus('loaded'); onLoad?.(e); }}
      onError={(e) => { setStatus('error'); onError?.(e); }}
      {...rest}
    />
  );
});

export default BackendImage;
